<template>
  <div class="dashboard-page">

    <!-- Welcome Banner -->
    <div class="dashboard-welcome-banner">
      <div class="banner-bg-image"></div>
      <div class="banner-content">
        <h2 class="banner-title">Welcome back! 👋</h2>
        <p class="banner-subtitle">Here's what's happening with animal rescue efforts.</p>
      </div>
    </div>

    <!-- Stat Cards -->
    <div class="stat-cards-row">
      <!-- Total Cases -->
      <div class="stat-card">
        <div class="stat-card-header">
          <div class="stat-icon icon-cases">
            <CIcon name="cil-clipboard" />
          </div>
          <span class="stat-label">Total Cases</span>
        </div>
        <div class="stat-card-body">
          <span class="stat-value" v-if="!numberLoading">{{dashboardData['numbers']['recordCount'] | formatNumber}}</span>
          <pulse-loader :loading="numberLoading" color="#2E7D32"></pulse-loader>
          <div class="stat-sparkline">
            <svg viewBox="0 0 70 30" preserveAspectRatio="none">
              <polyline fill="none" stroke="#2E7D32" stroke-width="2" points="0,25 10,20 20,22 30,15 40,18 50,10 60,12 70,5"/>
            </svg>
          </div>
        </div>
        <div class="stat-card-footer">
          <span class="stat-change positive">▲ 12.6%</span>
          <span class="stat-period">vs last 12 months</span>
        </div>
      </div>

      <!-- Animal Saved -->
      <div class="stat-card">
        <div class="stat-card-header">
          <div class="stat-icon icon-saved">
            <CIcon name="cil-globe-alt" />
          </div>
          <span class="stat-label">Animal Saved</span>
        </div>
        <div class="stat-card-body">
          <span class="stat-value" v-if="!numberLoading">{{dashboardData['numbers']['animalsSaved']}}</span>
          <pulse-loader :loading="numberLoading" color="#2E7D32"></pulse-loader>
          <div class="stat-sparkline">
            <svg viewBox="0 0 70 30" preserveAspectRatio="none">
              <polyline fill="none" stroke="#00897B" stroke-width="2" points="0,20 10,18 20,22 30,12 40,15 50,8 60,10 70,5"/>
            </svg>
          </div>
        </div>
        <div class="stat-card-footer">
          <span class="stat-change positive">▲ 8.3%</span>
          <span class="stat-period">vs last 12 months</span>
        </div>
      </div>

      <!-- Species Handled -->
      <div class="stat-card">
        <div class="stat-card-header">
          <div class="stat-icon icon-species">
            <CIcon name="cil-paw" />
          </div>
          <span class="stat-label">Species Handled</span>
        </div>
        <div class="stat-card-body">
          <span class="stat-value" v-if="!numberLoading">{{dashboardData['numbers']['uniqueSpecies'].length}}</span>
          <pulse-loader :loading="numberLoading" color="#2E7D32"></pulse-loader>
          <div class="stat-sparkline">
            <svg viewBox="0 0 70 30" preserveAspectRatio="none">
              <polyline fill="none" stroke="#2E7D32" stroke-width="2" points="0,22 10,18 20,20 30,14 40,16 50,12 60,8 70,6"/>
            </svg>
          </div>
        </div>
        <div class="stat-card-footer">
          <span class="stat-change positive">▲ 5.7%</span>
          <span class="stat-period">vs last 12 months</span>
        </div>
      </div>

      <!-- Cases This Month -->
      <div class="stat-card">
        <div class="stat-card-header">
          <div class="stat-icon icon-month">
            <CIcon name="cil-calendar" />
          </div>
          <span class="stat-label">Cases This Month</span>
        </div>
        <div class="stat-card-body">
          <span class="stat-value" v-if="!numberLoading">{{dashboardData['numbers']['recordsThisMonth']}}</span>
          <pulse-loader :loading="numberLoading" color="#2E7D32"></pulse-loader>
          <div class="stat-sparkline">
            <svg viewBox="0 0 70 30" preserveAspectRatio="none">
              <polyline fill="none" stroke="#1565C0" stroke-width="2" points="0,15 10,15 20,15 30,15 40,15 50,15 60,15 70,15"/>
            </svg>
          </div>
        </div>
        <div class="stat-card-footer">
          <span class="stat-change neutral">— 0%</span>
          <span class="stat-period">vs last month</span>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <CRow>
      <!-- Zone Distribution -->
      <CCol md="5">
        <div class="chart-card">
          <div class="chart-card-header">
            <div class="chart-title-area">
              <h4 class="chart-title">
                Zone Distribution
                <span class="info-icon" title="Distribution of cases across zones">i</span>
              </h4>
              <div class="chart-subtitle">Total Cases by Zone</div>
            </div>
            <div class="chart-actions">
              <button class="chart-menu-btn" title="More options">⋮</button>
            </div>
          </div>
          <div class="chart-body">
            <div v-if="numberLoading" class="loader-wrapper">
              <pulse-loader :loading="numberLoading" color="#2E7D32"></pulse-loader>
            </div>
            <div v-else class="zone-chart-content">
              <div class="zone-donut-wrapper">
                <donut-chart 
                  id="casesByZone"
                  :data="sortedCasesByZone"
                  xkey="label"
                  ykeys='[ "value" ]'
                  :colors="zoneColors"
                  grid-text-weight="bold"
                  resize="true">
                </donut-chart>
                <div class="donut-center-text" v-if="casesByZone.length > 0">
                  <div class="donut-center-zone">{{ topZoneName }}</div>
                  <div class="donut-center-count">{{ totalZoneCases | formatNumber }}</div>
                  <div class="donut-center-label">Total Cases</div>
                </div>
              </div>
              <div class="zone-legend">
                <div class="zone-legend-item" v-for="(zone, idx) in zoneLegendData" :key="idx">
                  <span class="zone-dot" :style="{ background: zoneColors[idx] || '#ccc' }"></span>
                  <span class="zone-name">{{ zone.name }}</span>
                  <span class="zone-value">{{ zone.count | formatNumber }} ({{ zone.percentage }}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CCol>

      <!-- New Cases Handled -->
      <CCol md="7">
        <div class="chart-card">
          <div class="chart-card-header">
            <div class="chart-title-area">
              <h4 class="chart-title">New Cases Handled</h4>
              <div class="chart-subtitle">Past 12 months</div>
            </div>
            <div class="chart-actions">
              <button class="chart-refresh-btn" @click="getStatNumbers()" title="Refresh data">
                <CIcon name="cil-reload" />
              </button>
            </div>
          </div>
          <div class="chart-body">
            <div v-if="numberLoading" class="loader-wrapper">
              <pulse-loader :loading="numberLoading" color="#2E7D32"></pulse-loader>
            </div>
            <line-chart 
              v-else
              id="sighting"
              :data="newCases"
              xkey="date"
              ykeys='[ "count" ]'
              xLabels = "month"
              :xLabelFormat="getMonthLabel"
              :hoverCallback="hoverCallBack"
              line-colors='[ "#2E7D32" ]' 
              grid="true"
              grid-text-weight="bold"
              resize="true">
            </line-chart>
          </div>
        </div>
      </CCol>
    </CRow>

    <!-- Animal Classification -->
    <div class="chart-card classification-card">
      <div class="classification-header">
        <div class="classification-title-area">
          <h4 class="classification-title">Animal Classification wise</h4>
          <span class="info-icon" title="Animal classification breakdown">i</span>
          <button 
            class="back-btn" 
            v-if="filterForClass != '$class'" 
            @click="updateClassFilterBack"
            title="Go back"
          >
            <CIcon name="cil-arrow-left"/>
          </button>
        </div>
        <div class="classification-filter">
          <CSelect
            :options="optionsForClass"
            :value.sync="optionsSelected"
            @change="updateClassFilter"
            placeholder="Select an option to change value"
          />
        </div>
      </div>
      <div class="chart-body">
        <div v-if="loading" class="loader-wrapper">
          <pulse-loader :loading="loading" color="#2E7D32"></pulse-loader>
        </div>
        <bar-chart 
          v-else
          id="statsByClass"
          :data="statsByClass"
          xkey="name"
          ykeys='[ "count" ]'
          :barColors="classBarColors"
          grid="true"
          grid-text-weight="bold"
          :xLabelFormat="getByClassLabel"
          :hoverCallback="hoverCallBackClass"
          resize="true">
        </bar-chart>
      </div>
    </div>

  </div>
</template>

<script>
import Raphael from 'raphael/raphael'
global.Raphael = Raphael
import { DonutChart, BarChart, LineChart, AreaChart } from 'vue-morris'

if (typeof window !== 'undefined') {
  // Override Morris Donut redraw method to increase donut segment thickness
  const overrideMorris = () => {
    if (window.Morris && window.Morris.Donut) {
      window.Morris.Donut.prototype.redraw = function() {
        var C, cx, cy, i, idx, last, max_value, min, next, seg, total, value, w, _i, _j, _k, _len, _len1, _len2, _ref, _ref1, _ref2, _results;
        this.raphael.clear();
        cx = this.el.width() / 2;
        cy = this.el.height() / 2;
        w = (Math.min(cx, cy) - 10) / 3;
        total = 0;
        _ref = this.values;
        for (_i = 0, _len = _ref.length; _i < _len; _i++) {
          value = _ref[_i];
          total += value;
        }
        min = 5 / (2 * w);
        C = 1.9999 * Math.PI - min * this.data.length;
        last = 0;
        idx = 0;
        this.segments = [];
        _ref1 = this.values;
        for (i = _j = 0, _len1 = _ref1.length; _j < _len1; i = ++_j) {
          value = _ref1[i];
          next = last + min + C * (value / total);
          // w * 1.35 inner radius, w * 1.65 outer width (total outer radius w * 3.0)
          // This increases segment thickness significantly to match the figma UI perfectly
          seg = new window.Morris.DonutSegment(cx, cy, w * 1.35, w * 1.65, last, next, this.data[i].color || this.options.colors[idx % this.options.colors.length], this.options.backgroundColor, idx, this.raphael);
          seg.render();
          this.segments.push(seg);
          seg.on('hover', this.select);
          seg.on('click', this.click);
          last = next;
          idx += 1;
        }
        this.text1 = this.drawEmptyDonutLabel(cx, cy - 10, this.options.labelColor, 15, 800);
        this.text2 = this.drawEmptyDonutLabel(cx, cy + 10, this.options.labelColor, 14);
        max_value = Math.max.apply(Math, this.values);
        idx = 0;
        _ref2 = this.values;
        _results = [];
        for (_k = 0, _len2 = _ref2.length; _k < _len2; _k++) {
          value = _ref2[_k];
          if (value === max_value) {
            this.select(idx);
            break;
          }
          _results.push(idx += 1);
        }
        return _results;
      };
    }
  };
  
  // Try immediately and also on next ticks/window load
  overrideMorris();
  window.addEventListener('load', overrideMorris);
  setTimeout(overrideMorris, 100);
  setTimeout(overrideMorris, 1000);
}

import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import GridLoader from 'vue-spinner/src/GridLoader.vue'


import axios from 'axios';
import store from '../store';

export default {
  name: 'Dashboard',
  components: {
    LineChart,
    AreaChart,
    PulseLoader,
    GridLoader,
    BarChart,
    DonutChart
  },
  filters: {
    formatNumber(value) {
      if (!value && value !== 0) return '';
      return Number(value).toLocaleString();
    }
  },
  data () {
    return {
      baseURL : store.state.baseURL,
      selected: 'Month',
      users     : [],

      dashboardData : {},

      statsByClass : [],
      statsByClassFilter : {
        type : null,
        class : null,
        group : null
      },
      optionsForClass : [],
      optionsSelected : null,
      filterForClass : null,

      newCases : [],
      casesByZone : [],

      zoneColors: [
        '#1565C0',
        '#1E88E5',
        '#42A5F5',
        '#64B5F6',
        '#90CAF9',
        '#A6D4FA',
        '#C2E2FB',
        '#DDF0FF'
      ],

      classBarColors: ['#2E7D32', '#E65100', '#F9A825'],

      monthNames : [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ],

      lineChart : {
        dataSets : [],
        labels : []
      },

      tableFields: [
        { key: 'avatar', label: '', _classes: 'text-center' },
        { key: 'user' },
        { key: 'organisation', _classes: 'text-center' },
        { key: 'role', _classes: 'text-center' },
        { key: 'activity', _classes: 'text-center' }
      ],
      loading : true,
      numberLoading : true
    }
  },
  computed: {
    topZoneName() {
      if (this.casesByZone.length > 0) {
        let sorted = [...this.casesByZone].sort((a, b) => (b.value || 0) - (a.value || 0));
        return sorted[0].label || '';
      }
      return '';
    },
    totalZoneCases() {
      return this.casesByZone.reduce((sum, z) => sum + (z.value || 0), 0);
    },
    sortedCasesByZone() {
      if (!this.casesByZone.length) return [];
      return [...this.casesByZone].sort((a, b) => (b.value || 0) - (a.value || 0));
    },
    zoneLegendData() {
      if (!this.casesByZone.length) return [];
      let total = this.totalZoneCases;
      // Sort by value descending
      let sorted = [...this.casesByZone].sort((a, b) => (b.value || 0) - (a.value || 0));
      
      if (sorted.length <= 8) {
        return sorted.map(z => ({
          name: z.label,
          count: z.value || 0,
          percentage: total > 0 ? ((z.value / total) * 100).toFixed(1) : '0.0'
        }));
      }
      
      // Take top 7, aggregate rest as "Others"
      let top7 = sorted.slice(0, 7).map(z => ({
        name: z.label,
        count: z.value || 0,
        percentage: total > 0 ? ((z.value / total) * 100).toFixed(1) : '0.0'
      }));
      
      let othersCount = sorted.slice(7).reduce((sum, z) => sum + (z.value || 0), 0);
      top7.push({
        name: 'Others',
        count: othersCount,
        percentage: total > 0 ? ((othersCount / total) * 100).toFixed(1) : '0.0'
      });
      
      return top7;
    }
  },
  mounted(){
    this.getStatByClass();
    this.getStatNumbers();
  },
  methods: {
    updateClassFilterBack(){
      if(this.filterForClass == "$identityName"){
        //set to groupname
        this.statsByClassFilter.group = null;
        this.getStatByClass();
      }
      else if(this.filterForClass == "$groupName"){
        //set to class
        this.statsByClassFilter.class = null;        
        this.getStatByClass();
      }
      else{
        this.$notify({
            group: 'notification',
            type : 'error',
            title: 'Important message',
            text: 'Further classification is not possible!'
          });
      }
    },
    updateClassFilter(){
      if(this.filterForClass == "$class"){
        //set to groupname
        this.statsByClassFilter.class = this.optionsSelected;
        this.getStatByClass();
      }
      else if(this.filterForClass == "$groupName"){
        //set to identityName
        this.statsByClassFilter.group = this.optionsSelected;        
        this.getStatByClass();
      }
      else{
        this.$notify({
            group: 'notification',
            type : 'error',
            title: 'Important message',
            text: 'Further classification is not possible!'
          });
      }
    },
    hoverCallBack(index, options, content, row){
      var date = new Date(row.date);
      var fullYear = date.getFullYear();
      let label = this.monthNames[date.getMonth()] + "-" +fullYear + " : " + row.count + " Cases";
      return label;
    },
    hoverCallBackClass(index, options, content, row){
      let label = row.name.toUpperCase() + " : " + row.count + " Cases";
      return label;
    },
    getMonthLabel(x){
      var fullYear = x.getFullYear()%2000;
      let label = this.monthNames[x.getMonth()] + "-" +fullYear;
      return label;
    },
    getByClassLabel(x){
      return x.label.toUpperCase();
    },
    rand (limit) {
      return Math.round(Math.random() * limit)
    },
    getMonth(date){
      var arr = date.split("-");
      var months = [ "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December" ];
      var month_index =  parseInt(arr[1],10) - 1;
      return months[month_index];
    },
    getDate(date){
      var arr = date.split("-");
      var index =  parseInt(arr[2],10);
      return index;
    },
    approve(id){
      console.log(id);
    },
    color (value) {
      let $color
      if (value <= 25) {
        $color = 'info'
      } else if (value > 25 && value <= 50) {
        $color = 'success'
      } else if (value > 50 && value <= 75) {
        $color = 'warning'
      } else if (value > 75 && value <= 100) {
        $color = 'danger'
      }
      return $color
    },
    getStatByClass(){
      this.loading = true;
      axios({
          url : this.baseURL + "analytics/stats/by-class",
          headers : {
              authorization : store.state.token
          },
          data : this.statsByClassFilter,
          method : "POST"
      })
      .then(res => {
          if(res.data.status)
          {         
            this.optionsForClass = [];
            this.statsByClass = res.data.data;    
            this.filterForClass = res.data.filters.typeFilter;
            this.optionsForClass.push("--Select an option to change value--");
            for(var i =0; i<this.statsByClass.length; i++){
              this.optionsForClass.push(this.statsByClass[i].name);
            }
          }        
          this.loading = false;
      })
      .catch(err => {
          this.$notify({
            group: 'notification',
            type : 'warn',
            title: 'Important message',
            text: 'Error in loading data. Please check your internet connection!'
          });
          console.log(err);
          this.loading = false;
      });
    },
    getStatNumbers(){
      this.numberLoading = true;
      axios({
          url : this.baseURL + "analytics/stats/numbers",
          headers : {
              authorization : store.state.token
          },
          method : "GET"
      })
      .then(res => {
          if(res.status)
          {                           
            this.dashboardData  = res.data.data;   
            this.newCases = this.dashboardData.graphs.monthCounts;        
            this.casesByZone = this.dashboardData.graphs.zoneCounts;        
          }
          this.numberLoading = false;
      })
      .catch(err => {
          this.$notify({
            group: 'notification',
            type : 'warn',
            title: 'Important message',
            text: 'Error in loading data. Please check your internet connection!'
          });
          console.log(err);
          this.numberLoading = false;
      });
    },
  },
  
}
</script>

<style scoped>
.dashboard-page {
  padding: 4px 0;
}
</style>
